/**
 * Central media library — E & J Painting's real project photos,
 * hosted on Supabase. Every image on the site is pulled from here so
 * no photo is reused between sections.
 */

const SUPABASE_FULL =
  "https://sbdjxsxpkgngbkdabugx.supabase.co/storage/v1/object/public/client-media/e-j-painting/images";
const SUPABASE_RENDER =
  "https://sbdjxsxpkgngbkdabugx.supabase.co/storage/v1/render/image/public/client-media/e-j-painting/images";

/** Raw filenames, newest first — the order used by the gallery. */
export const photoFiles = [
  "1786190922306-281-1000015566.jpg",
  "1786190921821-708-1000015565.jpg",
  "1786190921226-186-1000028871.jpg",
  "1786190920446-978-1000028872.jpg",
  "1786190919586-205-1000041203.jpg",
  "1786190919015-418-1000041204.jpg",
  "1786190918378-736-1000049507.jpg",
  "1786190918033-530-1000064880.jpg",
  "1786190917696-31-1000064878.jpg",
  "1786190917351-958-1000064876.jpg",
  "1786190916950-405-1000064874.jpg",
  "1786190916474-651-1000018680.jpg",
  "1786190915995-247-1000018681.jpg",
  "1786190915342-53-1000018683.jpg",
  "1786190914915-455-1000018682.jpg",
  "1786190914575-7-1000056610.jpg",
  "1786190914267-716-1000056608.jpg",
  "1786190913922-6-1000056765.jpg",
  "1786190913624-985-1000056767.jpg",
  "1786190913312-941-1000061212.jpg",
  "1786190912889-105-1000061238.jpg",
  "1786190912464-353-1000061233.jpg",
  "1786190912174-472-1000061219.jpg",
  "1786190911833-279-1000061213.jpg",
  "1786190911470-758-1000064784.jpg",
  "1786190911074-837-1000064786.jpg",
  "1786190910685-998-1000064780.jpg",
  "1786190910346-857-1000064782.jpg",
  "1786190909962-685-1000064799.jpg",
  "1786190908923-762-1000064801.jpg",
] as const;

/** Full-resolution original — use for the gallery lightbox only (these run 1.5–3 MB). */
export const photoFull = (i: number) => `${SUPABASE_FULL}/${photoFiles[i]}`;

/**
 * Resized/optimized variant via Supabase's image renderer.
 * Keeps page weight down for cards and thumbnails.
 */
export const photo = (i: number, width = 800, height = 600, quality = 70) =>
  `${SUPABASE_RENDER}/${photoFiles[i]}?width=${width}&height=${height}&resize=cover&quality=${quality}`;

/**
 * Wide 16:9 variant for full-bleed section headers.
 * Card-sized images upscale badly across a 1920px hero, so headers must use this.
 * Pass `width` to cap the request at a photo's native width and avoid upscaling.
 */
/**
 * Quality 75 rather than 82: on the 1600px hero that is 163 kB instead of
 * 213 kB for no visible difference in a photograph shown behind a dark
 * overlay. Below 75 the curve flattens — 72 saves only another 5 kB.
 */
export const photoWide = (i: number, width = 1920) =>
  photo(i, width, Math.round((width * 9) / 16), 75);

/**
 * Candidate widths for a full-bleed header, so a phone is not made to download
 * a 1920px render it will paint into roughly 400 CSS pixels. Pair with
 * `sizes="100vw"` — the header always spans the viewport.
 */
export const photoWideSrcSet = (i: number, maxWidth = 1920) =>
  [640, 960, 1280, 1600]
    .filter((w) => w < maxWidth)
    .concat(maxWidth)
    .map((w) => `${photoWide(i, w)} ${w}w`)
    .join(", ");

/**
 * Same idea for the 4:3 card thumbnails used in the service and blog grids.
 * The widths track the sizes the cards actually render at inside the 1280px
 * container: ~400px for the three-up blog grid, ~612px for the two-up service
 * grid, and 800 to cover a full-width card on a high-density phone. The old
 * 400/600/800 ladder had no rung near 612, so the two-up cards always pulled
 * the 800px render for a 588px slot.
 */
export const photoCardSrcSet = (i: number) =>
  [400, 640, 800].map((w) => `${photo(i, w, Math.round((w * 3) / 4), 70)} ${w}w`).join(", ");

/** Square thumbnails used by the gallery grid. */
export const photoThumb = (i: number) => photo(i, 480, 480, 50);

export const allPhotosFull = photoFiles.map((_, i) => photoFull(i));
export const allPhotoThumbs = photoFiles.map((_, i) => photoThumb(i));

/**
 * Real stock photography, used only where the client's own library has no
 * matching shot. Unsplash License — free for commercial use, no attribution
 * required, hotlinking to images.unsplash.com is the documented delivery method.
 *
 * Every service card is covered by a client photo, so nothing here feeds one.
 */
const UNSPLASH = "https://images.unsplash.com";

export const stockPhotos = {
  /**
   * Painter patching and skim-coating an exterior wall — the client library has
   * no unused exterior prep shot. https://unsplash.com/photos/1613844044163
   */
  // `auto=format` lets Unsplash's CDN negotiate WebP/AVIF from the Accept
  // header. The previous `fm=jpg` pinned it to JPEG for every visitor.
  exteriorPrep: {
    card: `${UNSPLASH}/photo-1613844044163-1ad2f2d0b152?w=800&h=600&fit=crop&q=75&auto=format`,
    wide: `${UNSPLASH}/photo-1613844044163-1ad2f2d0b152?w=1920&h=1080&fit=crop&q=82&auto=format`,
  },
} as const;

/**
 * Fixed slot assignments. Every photo below was reviewed individually and
 * matched to its subject; no index is used twice.
 *
 *  00 teal interior, crown molding + chair rail, drop cloths    3264x2448
 *  01 teal interior with window, masked and in progress         3264x2448
 *  02 ceiling drywall patch + mudding, fixture bagged           4096x3072
 *  04 exterior siding, soffit, fascia, porch, windows masked    3264x2448
 *  05 two-storey home exterior mid-repaint, ladder + sprayer    3264x2448
 *  06 room fully masked in plastic and tape (prep)              3264x2448
 *  11 white kitchen cabinets and trim, finished                 3264x2448
 *  14 white kitchen, counters and sink, finished                3264x2448
 *  18 open-plan home mid-repaint, island + furniture masked     1600x1200
 *  28 commercial strip building, painted white brick            1024x768
 */
export const mediaSlots = {
  /** Home hero — the most representative shot: a house mid-repaint. */
  hero: 5,
  /** Order matches `services`: interior, exterior, residential, commercial, drywall. */
  services: [0, 4, 18, 28, 2],
  /** Services page header — interior work in progress. */
  servicesHero: 1,
  /** About header — meticulous masking, which is what that page is about. */
  aboutHero: 6,
  /**
   * Order matches `blogPosts`: repaint timing, paint finishes, exterior prep.
   * The third entry is unused — that post uses `stockPhotos.exteriorPrep`.
   */
  blog: [14, 11, 13],
} as const;

/**
 * Native width per slot, where the source is smaller than 1920. `photoWide`
 * is capped to these so a header never upscales into softness.
 */
export const nativeWideWidth: Record<number, number> = {
  18: 1600,
  28: 1024,
};
